import { inject, Injectable } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import type { Observable } from 'rxjs'
import { filter, map, shareReplay } from 'rxjs/operators'

import { truthy } from '@app/core/types/truthy'

interface AppLayoutConfig {
  header: boolean
  footer: FooterVariant
}

const DEFAULT_LAYOUT_CONFIG: AppLayoutConfig = {
  header: true,
  footer: 'simple',
}

export type FooterVariant = 'hidden' | 'simple' | 'full'

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  private readonly router = inject(Router)
  private readonly activatedRoute = inject(ActivatedRoute)

  readonly config$: Observable<AppLayoutConfig> = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(() => this.activatedRoute),
    map(route => {
      let config = this.overrideConfig(route, { ...DEFAULT_LAYOUT_CONFIG })
      let lastRoute = route
      while (lastRoute.firstChild) {
        lastRoute = lastRoute.firstChild
        config = this.overrideConfig(lastRoute, config)
      }
      return config
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  private overrideConfig(route: ActivatedRoute, config: AppLayoutConfig): AppLayoutConfig {
    if (route.outlet !== 'primary') {
      return config
    }

    return {
      header: truthy(route.snapshot.data?.header ?? config.header),
      footer: route.snapshot.data?.footer ?? config.footer,
    }
  }
}
